import React, { useEffect, useState } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, useLocation, useNavigate, Navigate } from 'react-router-dom';
import './App.css';
import Register from './components/Register';
import Login from './components/Login';
import OAuthCallback from './components/OAuthCallback';
import CameraMonitor from './components/CameraMonitor';
import AdminPanel from './components/AdminPanel';
import ForgotPassword from './components/ForgotPassword';
import ResetPassword from './components/ResetPassword';
import VerifyEmail from './components/VerifyEmail';
import { api } from './api/client';
import { computeGraceEndMs } from './utils/time';
import { FocusScoreDebug } from './components/DevTools';
import { ThemeContext } from './contexts/ThemeContext';
import { storage } from './utils/storage';
import SyncIndicator from './components/SyncIndicator';
import ErrorBoundary from './components/ErrorBoundary';

const Account = React.lazy(() => import('./components/Account'));
const SoloSessionRoom = React.lazy(() => import('./components/SoloSessionRoom'));
const PublicSessionRoom = React.lazy(() => import('./components/PublicSessionRoom'));
const PhoneZen = React.lazy(() => import('./components/PhoneZen'));
const CommunitySchedule = React.lazy(() => import('./components/CommunitySchedule'));
const Analytics = React.lazy(() => import('./components/Analytics'));
const BlogList = React.lazy(() => import('./components/BlogList'));
const BlogPost = React.lazy(() => import('./components/BlogPost'));
const AdminBlog = React.lazy(() => import('./components/AdminBlog'));
const PrivacyPolicy = React.lazy(() => import('./components/PrivacyPolicy'));
const TermsOfService = React.lazy(() => import('./components/TermsOfService'));

const Home: React.FC = () => {
  const { theme } = React.useContext(ThemeContext);
  const [heroUrl, setHeroUrl] = useState<string>('/hero.jpg');
  useEffect(() => {
    let cancelled = false;
    const tryLoad = (src: string) => new Promise<boolean>((resolve) => {
      const img = new Image();
      img.onload = () => resolve(true);
      img.onerror = () => resolve(false);
      img.src = src;
    });
    (async () => {
      const candidates = ['/hero.jpg', '/hero.png', '/images/hero.jpg', '/images/hero.png'];
      for (const c of candidates) {
        const ok = await tryLoad(c);
        if (ok) {
          if (!cancelled) setHeroUrl(c);
          return;
        }
      }
      if (!cancelled) setHeroUrl('/logo512.png');
    })();
    return () => { cancelled = true; };
  }, []);
  const overlay = theme === 'light'
    ? 'radial-gradient(900px 400px at 50% -200px, rgba(255,255,255,0.55), transparent), linear-gradient(180deg, rgba(248, 251, 255, 0.9) 0%, rgba(238, 244, 255, 0.8) 60%, rgba(232, 239, 255, 0.9) 100%)'
    : 'radial-gradient(900px 400px at 50% -200px, rgba(10, 20, 40, 0.65), transparent), linear-gradient(180deg, rgba(8, 12, 22, 0.85) 0%, rgba(8, 12, 22, 0.7) 55%, rgba(8, 12, 22, 0.85) 100%)';
  const bgStyle: React.CSSProperties = {
    backgroundImage: overlay,
    backgroundRepeat: 'no-repeat',
    backgroundSize: 'cover',
    backgroundPosition: 'center top',
  };
  return (
    <div className="landing-bg" style={bgStyle}>
      <main id="main-content" role="main">
        <section id="hero" className="hero-wrap">
          <div className="container text-center py-5">
            <h1 className="hero-title mb-3">
              Focus <span className="accent">Better.</span>
            </h1>
            <p className="hero-sub lead mb-4">
              Stay in flow at your desk. On-device computer vision tracks your focus in Solo and optional group video sessions, with attention data processed locally and video never sent to our servers.
            </p>
            <div className="d-flex justify-content-center gap-3">
              <Link to="/register" className="btn btn-cta btn-lg">Try FocusBees for Free</Link>
            </div>
          </div>
          <div className="hero-media">
            <div className="hero-media-card">
              <img src={heroUrl} alt="FocusBees product preview" className="hero-device" />
            </div>
          </div>
        </section>

        <section id="session-modes" className="section-wrap">
          <div className="container">
            <div className="glass-card p-4 p-md-5">
              <div className="text-center mb-4">
                <h2 className="section-title">Solo or Group, same flow state</h2>
                <p className="lead text-muted">
                  Choose how you want to work today. The same local CV metric engine powers both modes; Group sessions simply add video co-working for extra accountability.
                </p>
              </div>
              <div className="row g-4">
                <div className="col-md-6">
                  <div className="tile-card h-100 text-start">
                    <div className="h2 mb-2">🧑‍💻 Solo Sessions</div>
                    <p className="text-muted mb-3">
                      Your always-on focus body double that runs entirely on your device. All focus metrics are computed locally in the browser. Perfect for deep work sprints, studying, or ship-it days.
                    </p>
                    <ul className="list-unstyled text-muted small mb-0">
                      <li>• Timebox focused work with clear start and end.</li>
                      <li>• Get gentle nudges when you drift to your phone or another screen.</li>
                      <li>• See how your focus improves over time with rich analytics.</li>
                    </ul>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="tile-card h-100 text-start">
                    <div className="h2 mb-2">👥 Group Sessions</div>
                    <p className="text-muted mb-3">
                      Video co-working rooms with structured check-ins, minimal talking, and shared momentum, like going to a workout class for your brain. The same local CV metric engine runs on your device; video is only for presence and accountability.
                    </p>
                    <ul className="list-unstyled text-muted small mb-0">
                      <li>• Join hosts and peers for live, time-boxed focus blocks.</li>
                      <li>• Share your goals at the start and celebrate progress at the end.</li>
                      <li>• Body doubling that pairs social presence with the same focus rituals you use in Solo Sessions.</li>
                    </ul>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="how-it-works-steps" className="section-wrap">
          <div className="container">
            <div className="glass-card p-4 p-md-5">
              <div className="text-center mb-5">
                <h2 className="section-title">How It Works</h2>
                <p className="lead text-muted">Three simple steps to get into flow and stay there</p>
              </div>
              <div className="row g-4">
                <div className="col-md-4">
                  <div className="tile-card h-100">
                    <div className="display-4 mb-3">📷</div>
                    <h3 className="h5 fw-bold mb-2">Enable Camera</h3>
                    <p className="text-muted">Secure, browser-based permission. Point your camera at yourself and start working.</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="tile-card h-100">
                    <div className="display-4 mb-3">🧠</div>
                    <h3 className="h5 fw-bold mb-2">Computer Vision Tracks Focus</h3>
                    <p className="text-muted">Camera-based focus tracking detects phone usage, drowsiness, and posture in real-time.</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="tile-card h-100">
                    <div className="display-4 mb-3">📈</div>
                    <h3 className="h5 fw-bold mb-2">Build Peak Focus</h3>
                    <p className="text-muted">Get nudges when you drift and detailed reports when you finish.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Features Built for Focus */}
        <section id="features" className="section-wrap">
          <div className="container">
            <div className="glass-card p-4 p-md-5">
              <div className="text-center mb-5">
                <h2 className="section-title">Features Built for Focus</h2>
                <p className="lead text-muted">Everything you need to maximize flow state and minimize distractions</p>
              </div>
              <div className="row g-4 justify-content-center">
                <div className="col-md-4">
                  <div className="tile-card h-100">
                    <div className="h2 mb-3">📵</div>
                    <h3 className="h5 fw-bold mb-2">Distraction Alerts</h3>
                    <p className="text-muted">Nudges you if you pick up your phone during sessions.</p>
                  </div>
                </div>

                <div className="col-md-4">
                  <div className="tile-card h-100">
                    <div className="h2 mb-3">📊</div>
                    <h3 className="h5 fw-bold mb-2">Real-time Feedback</h3>
                    <p className="text-muted">Live focus score and gentle cues keep you in the zone.</p>
                  </div>
                </div>
                <div className="col-md-4">
                  <div className="tile-card h-100">
                    <div className="h2 mb-3">🪑</div>
                    <h3 className="h5 fw-bold mb-2">Posture Tracking</h3>
                    <p className="text-muted">Gentle reminders to maintain healthy posture.</p>
                  </div>
                </div>
              </div>
              <div className="row g-3 mt-4 justify-content-center">
                <div className="col-md-6">
                  <div className="mini-card text-start">
                    <div className="small text-muted mb-1">New • Phone Companion</div>
                    <div className="d-flex align-items-center justify-content-between">
                      <div className="me-3">
                        <div className="fw-semibold mb-1">Zen Mode</div>
                        <div className="text-muted small">
                          Pair your phone as a quiet presence so you stay off it while you work.
                        </div>
                      </div>
                      <div style={{ fontSize: '2rem' }}>📱</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="analytics" className="section-wrap">
          <div className="container">
            <div className="glass-card p-4 p-md-5">
              <div className="row g-4 align-items-center">
                <div className="col-md-6">
                  <h2 className="section-title mb-3">Analytics for deep focus</h2>
                  <p className="lead text-muted">
                    See how your focus improves over time with clear, actionable stats instead of noisy dashboards.
                  </p>
                  <ul className="list-unstyled text-muted mb-0">
                    <li className="mb-2">• Monthly breakdown of focused time, phone use, and partial attendance.</li>
                    <li className="mb-2">• Reliability score based on attendance, on-time starts, and no-shows.</li>
                    <li className="mb-2">• Session summaries powered by the same local computer vision engine that watches your attention.</li>
                  </ul>
                </div>
                <div className="col-md-6">
                  <div className="tile-card h-100">
                    <div className="h2 mb-3">📈</div>
                    <h3 className="h5 fw-bold mb-2">Your focus story, visualized</h3>
                    <p className="text-muted mb-3">
                      Lightweight charts highlight what matters most: when you focus best, what breaks it,
                      and how consistent you are week over week.
                    </p>
                    <div className="d-flex flex-wrap gap-2">
                      <span className="badge bg-honey text-dark">This Month Breakdown</span>
                      <span className="badge bg-secondary">Reliability</span>
                      <span className="badge bg-secondary">Session Summaries</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>

        <section id="faq" className="section-wrap">
          <div className="container">
            <div className="glass-card p-4 p-md-5">
              <div className="text-center mb-5">
                <h2 className="section-title">Frequently Asked Questions</h2>
                <p className="lead text-muted">
                  Flow state, local CV metrics, Solo vs Group sessions—here’s how FocusBees fits together.
                </p>
              </div>

              <div className="mb-3">
                <details>
                  <summary className="d-flex justify-content-between align-items-center faq-summary">
                    <span className="h6 mb-0">What is flow state?</span>
                    <span className="faq-toggle-icon">+</span>
                  </summary>
                  <div className="mt-2">
                    <p className="text-muted mb-2">
                      Psychologists describe flow as a state where you are so absorbed in what you’re doing that
                      everything else fades into the background. Time moves differently, self-consciousness drops,
                      and you feel both challenged and capable—what many people call being “in the zone.”
                    </p>
                    <p className="text-muted mb-0">
                      FocusBees is designed to help you build more of these moments into your everyday work by pairing
                      clear timeboxes with real-time feedback and gentle accountability.
                    </p>
                  </div>
                </details>
              </div>

              <div className="mb-3">
                <details>
                  <summary className="d-flex justify-content-between align-items-center faq-summary">
                    <span className="h6 mb-0">How do I get into flow with FocusBees?</span>
                    <span className="faq-toggle-icon">+</span>
                  </summary>
                  <div className="mt-2">
                    <p className="text-muted mb-2">
                      Flow looks different for everyone, but research points to a few reliable ingredients. FocusBees is built
                      to support these:
                    </p>
                    <ul className="text-muted mb-0">
                      <li className="mb-1">
                        <strong>Challenge–skill balance:</strong> Pick work that’s slightly stretching, not overwhelming. Use tags and the in-session task list to define one concrete slice of work per session.
                      </li>
                      <li className="mb-1">
                        <strong>Clear, attainable goals:</strong> Turn “work on project” into “ship this section” or “write for 30 minutes.” Sessions are time-based by default, so you can focus on showing up, not over-planning.
                      </li>
                      <li className="mb-1">
                        <strong>Unambiguous feedback:</strong> CV-based Focus Score, distraction tracking, and session summaries show you how present you actually were—not just how long the timer ran.
                      </li>
                      <li className="mb-1">
                        <strong>Time-based goals:</strong> Choose a 25, 50, or 90-minute block and let FocusBees handle the structure. You commit to the window; the product nudges you to stay with it.
                      </li>
                      <li className="mb-1">
                        <strong>Follow your energy:</strong> Use Solo sessions when you’re in a deep-work mood, and join Group sessions when you want extra social momentum or body doubling.
                      </li>
                      <li className="mb-1">
                        <strong>Eliminate distractions:</strong> Phone Zen Mode helps you keep your phone out of the loop, while the camera-based CV model notices when you drift away from your desk.
                      </li>
                      <li>
                        <strong>Intentional environment:</strong> Treat each session as a small ritual—open FocusBees, set a goal, start the timer, and let the system handle the structure around you.
                      </li>
                    </ul>
                  </div>
                </details>
              </div>

              <div className="mb-3">
                <details>
                  <summary className="d-flex justify-content-between align-items-center faq-summary">
                    <span className="h6 mb-0">What are the benefits of using FocusBees regularly?</span>
                    <span className="faq-toggle-icon">+</span>
                  </summary>
                  <div className="mt-2">
                    <p className="text-muted mb-2">
                      Members use FocusBees to create a more reliable focus rhythm in their week. Over time, you can expect:
                    </p>
                    <ul className="text-muted mb-0">
                      <li className="mb-1">Sharpened focus and fewer “where did my day go?” moments.</li>
                      <li className="mb-1">Minimized distractions, especially around phone and off-task browsing.</li>
                      <li className="mb-1">ADHD-friendly scaffolding without needing to be “on” socially all the time.</li>
                      <li className="mb-1">A clearer sense of progress from session-to-session and month-to-month.</li>
                      <li className="mb-1">Motivation on demand from timeboxing and, when you want it, Group sessions.</li>
                      <li className="mb-1">More time for deep work, side projects, and actual rest.</li>
                      <li className="mb-1">Increased confidence from seeing your focus data improve over time.</li>
                      <li>Lower stress from knowing you have a simple system to get back on track.</li>
                    </ul>
                  </div>
                </details>
              </div>

              <div>
                <details>
                  <summary className="d-flex justify-content-between align-items-center faq-summary">
                    <span className="h6 mb-0">How does FocusBees actually work under the hood?</span>
                    <span className="faq-toggle-icon">+</span>
                  </summary>
                  <div className="mt-2">
                    <p className="text-muted mb-2">
                      FocusBees combines timeboxed sessions with local computer vision and optional group accountability:
                    </p>
                    <ul className="text-muted mb-0">
                      <li className="mb-1">
                        In every session, a CV model running in your browser (not the cloud) tracks signals like phone use,
                        being away from your desk, and drowsiness to estimate your Focus Score.
                      </li>
                      <li className="mb-1">
                        In Solo mode, only you see this data; it stays on your device and is summarized into metrics that power your Analytics.
                      </li>
                      <li className="mb-1">
                        In Group mode, you work alongside others in a lightweight video room. The video is for presence and accountability only—CV focus metrics are still computed locally on your machine.
                      </li>
                      <li>
                        Over time, your session history reveals patterns: when you focus best, how long your ideal blocks are, and what habits support your best work.
                      </li>
                    </ul>
                  </div>
                </details>
              </div>
            </div>
          </div>
        </section>

        {/* Section 5: Privacy & Technology */}
        <section id="privacy" className="section-wrap">
          <div className="container">
            <div className="glass-card p-4 p-md-5">
              <div className="row g-3 align-items-center">
                <div className="col-md-8">
                  <h2 className="section-title mb-2">Privacy & <span className="accent">Technology</span></h2>
                  <p className="text-muted mb-4">FocusBees computes all CV-based focus metrics 100% locally on your device for both Solo and Group sessions.</p>
                  <p className="lead">
                    Our camera-based focus tracking detects phone usage, drowsiness, and distraction in real-time without ever streaming video or focus metrics to a server. Group rooms add video conferencing for accountability, but all computer vision processing stays on your machine.
                  </p>
                </div>
                <div className="col-md-4 text-center">
                  <div className="display-1" style={{ fontSize: '8rem', lineHeight: 1 }}>🛡️</div>
                  <h3 className="fw-bold mt-3">Zero Privacy Risk</h3>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Section 4: Use Cases */}
        <section className="section-wrap py-5">
          <div className="container">
            <div className="glass-card p-5 text-center position-relative overflow-hidden">
              <div className="position-relative z-1">
                <h2 className="display-5 fw-bold mb-3">Ready to reclaim your focus?</h2>
                <p className="lead mb-4" style={{ maxWidth: '600px', margin: '0 auto' }}>
                  Start focusing with FocusBees today. Currently 100% free.
                </p>
                <div className="d-flex justify-content-center gap-3">
                  <Link to="/register" className="btn btn-cta btn-lg px-5">Try FocusBees for Free</Link>
                  <Link to="/login" className="btn btn-outline-light btn-lg px-5 d-flex align-items-center justify-content-center">Login</Link>
                </div>
                <p className="mt-3 text-muted small">
                  <span>✓ 100% Private & Local</span>
                </p>
              </div>
            </div>
          </div>
        </section>

      </main>

      <footer className="footer-wrap">
        <div className="container py-3 d-flex flex-column flex-md-row justify-content-between align-items-center gap-3">
          <div className="d-flex align-items-center gap-2">
            <img src="/logo192.png" alt="Logo" width="24" height="24" />
            <div className="fw-semibold">Focus<span className="accent">Bees</span></div>
            <div className="text-muted small">© FocusBees 2026. All rights reserved.</div>
          </div>
          <div className="d-flex flex-column flex-md-row align-items-center gap-3">
            <div className="d-flex flex-wrap justify-content-center align-items-center gap-3">
              <a href="mailto:support@focusbees.com" className="text-muted text-decoration-none">Contact Us</a>
              <Link to="/privacy" className="text-muted text-decoration-none">Privacy Policy</Link>
              <Link to="/terms" className="text-muted text-decoration-none">Terms</Link>
              <Link to="/blog" className="text-muted text-decoration-none">Blog</Link>
              <a href="https://x.com/FocusBees" className="text-muted text-decoration-none" aria-label="X">
                <svg viewBox="0 0 24 24" width="20" height="20" fill="currentColor">
                  <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
                </svg>
              </a>
            </div>
            <a href="https://dang.ai/" target="_blank" rel="noreferrer">
              <img src="https://cdn.prod.website-files.com/63d8afd87da01fb58ea3fbcb/6487e2868c6c8f93b4828827_dang-badge.png" alt="Dang.ai" style={{ width: '150px', height: '54px' }} width="150" height="54" />
            </a>
          </div>
        </div>
      </footer>
    </div>
  );
};

const RequireAuth: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const authed = !!storage.getString('userId');
  return authed ? <>{children}</> : <Navigate to="/login" replace />;
};

const NotFound: React.FC = () => (
  <div className="container mt-5 text-center">
    <h2>Page Not Found</h2>
    <p className="lead">The page you are looking for does not exist.</p>
    <Link to="/" className="btn btn-honey">Go Home</Link>
  </div>
);

const TopNav: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const [navSession, setNavSession] = useState<any>(null);
  const [navActiveList, setNavActiveList] = useState<Array<{ id: number; public_id?: string; is_public?: boolean; start_time: string; end_time?: string }>>([]);
  const [nowTs, setNowTs] = useState<number>(() => Date.now());
  const [navTier, setNavTier] = useState<string | null>(null);
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);

  useEffect(() => {
    const handler = (e: any) => {
      e.preventDefault();
      setDeferredPrompt(e);
    };
    window.addEventListener('beforeinstallprompt', handler);
    return () => window.removeEventListener('beforeinstallprompt', handler);
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    deferredPrompt.prompt();
    const { outcome } = await deferredPrompt.userChoice;
    if (outcome === 'accepted') {
      setDeferredPrompt(null);
    }
  };
  const isOnSessionRoute = /^\/(publicsession|solosession)\/\d+/.test(location.pathname);
  
  useEffect(() => {
    const i = setInterval(() => setNowTs(Date.now()), 1000);
    return () => clearInterval(i);
  }, []);

  useEffect(() => {
    const match = location.pathname.match(/^\/(?:solosession|publicsession)\/(\d+)/);
    if (!match) {
      setNavSession(null);
      return;
    }
    const sid = match[1];
    const loadSession = async () => {
      try {
        const res = await api.get(`/session/${sid}`, { skipAuthRedirect: true } as any);
        setNavSession(res.data.session);
      } catch (e) {
        setNavSession(null);
      }
    };
    loadSession();
  }, [location]);

  useEffect(() => {
    const uid = storage.getString('userId');
    if (!uid) { setNavActiveList([]); return; }
    (async () => {
      try {
        const res = await api.get(`/sessions/${uid}`, { params: { active: 'true', limit: '100', offset: '0' }, skipAuthRedirect: true } as any);
        const list: Array<{ id: number; public_id?: string; is_public?: boolean; start_time: string; end_time?: string }> = res.data?.sessions || [];
        setNavActiveList(list);
      } catch {
        setNavActiveList([]);
      }
    })();
  }, [location.pathname]);

  useEffect(() => {
    const uid = storage.getString('userId');
    if (!uid) {
      setNavTier(null);
      return;
    }
    let stored: string | null = null;
    try {
      stored = localStorage.getItem('userTier');
    } catch {}
    if (stored === 'free' || stored === 'paid') {
      setNavTier(stored);
      return;
    }
    (async () => {
      try {
        const usage = await api.get(`/users/${uid}/usage`, { skipAuthRedirect: true } as any);
        const rawTier = usage.data?.tier;
        const t = typeof rawTier === 'string' ? rawTier : null;
        if (t === 'free' || t === 'paid') {
          setNavTier(t);
          try {
            localStorage.setItem('userTier', t);
          } catch {}
        } else {
          setNavTier(null);
        }
      } catch {
        setNavTier(null);
      }
    })();
  }, []);

  useEffect(() => {
    let last = navTier;
    const i = setInterval(() => {
      try {
        const v = localStorage.getItem('userTier');
        const t = v === 'free' || v === 'paid' ? v : null;
        if (t !== last) {
          last = t;
          setNavTier(t);
        }
      } catch {}
    }, 1500);
    return () => clearInterval(i);
  }, [navTier]);

  const navOngoingLatest = React.useMemo(() => {
    const now = nowTs;
    const candidates = navActiveList.filter((s) => {
      const st = new Date(s.start_time).getTime();
      const en = s.end_time ? new Date(s.end_time).getTime() : (st + 60 * 60000);
      return now >= st && now < en;
    }).sort((a, b) => new Date(b.start_time).getTime() - new Date(a.start_time).getTime());
    return candidates[0] || null;
  }, [navActiveList, nowTs]);

  useEffect(() => {
    const uid = storage.getString('userId');
    if (!uid) return;
    const refresh = async () => {
      try {
        const res = await api.get(`/sessions/${uid}`, { params: { active: 'true', limit: '100', offset: '0' }, skipAuthRedirect: true } as any);
        const list: Array<{ id: number; public_id?: string; is_public?: boolean; start_time: string; end_time?: string }> = res.data?.sessions || [];
        setNavActiveList(list);
      } catch {
        setNavActiveList([]);
      }
    };
    const handler = () => refresh();
    window.addEventListener('sessions-changed', handler as any);
    const i = setInterval(refresh, 5000);
    return () => {
      window.removeEventListener('sessions-changed', handler as any);
      clearInterval(i);
    };
  }, [location.pathname]);

  useEffect(() => {
    const playSessionEndSound = () => {
      try {
        const audio = new Audio('/SessionEnd.mp3');
        audio.play().catch(e => console.error('Error playing session end sound:', e));
      } catch (e) {
        console.error('Error initializing session end sound:', e);
      }
    };

    const match = location.pathname.match(/^\/(?:solosession|publicsession)\/(\d+)/);
    if (!match) return;
    const sid = match[2];
    let startMs: number | undefined;
    let endMs: number | undefined;
    try {
      const gs = (typeof window !== 'undefined' && (window as any).__sessionStartMs) || undefined;
      const ge = (typeof window !== 'undefined' && (window as any).__sessionEndMs) || undefined;
      if (typeof gs === 'number' && typeof ge === 'number') {
        startMs = gs;
        endMs = computeGraceEndMs(gs, ge);
      } else if (navSession) {
        const s = new Date(navSession.start_time).getTime();
        const raw = navSession.end_time ? new Date(navSession.end_time).getTime() : (s + 60 * 60000);
        startMs = s;
        endMs = computeGraceEndMs(s, raw);
      } else {
        const rawTimes = localStorage.getItem(`sessionTimes:${sid}`);
        if (rawTimes) {
          const t = JSON.parse(rawTimes);
          if (typeof t.startMs === 'number' && typeof t.endMs === 'number') {
            startMs = t.startMs;
            endMs = computeGraceEndMs(t.startMs, t.endMs);
            try { (window as any).__sessionStartMs = t.startMs; (window as any).__sessionEndMs = t.endMs; } catch {}
          }
        }
      }
    } catch {}
    if (!startMs || !endMs) return;
    try {

      const ep = localStorage.getItem(`soundEndPlayed:${sid}`) === '1';

      if (!ep && nowTs >= endMs) {
        playSessionEndSound();
        localStorage.setItem(`soundEndPlayed:${sid}`, '1');
      }
    } catch {}
  }, [location.pathname, nowTs, navSession]);

  const authed = !!storage.getString('userId');
  const isPlusUser = navTier === 'paid';
  let brandTarget = '/';
  if (authed) {
    brandTarget = '/schedule';
  }

  return (
    <header role="banner">
    <nav className="navbar navbar-expand-lg navbar-light navbar-glass" id="top-nav">
      <div className="container-fluid">
        <Link 
          className="navbar-brand d-flex align-items-center gap-2" 
          to={authed ? brandTarget : "/"}
          onClick={(e) => {
            if (authed) {
              e.preventDefault();
              navigate(brandTarget);
            }
          }}
        >
          <img src="/logo192.png" alt="FocusBees Logo" width="32" height="32" className="d-inline-block align-text-top" />
          <span className="d-flex align-items-baseline gap-1">
            <span>Focus<span className="accent">Bees</span></span>
            {isPlusUser && <span className="text-white">Plus</span>}
          </span>
        </Link>
        <button className="navbar-toggler" type="button" data-bs-toggle="collapse" data-bs-target="#navbarNav" aria-controls="navbarNav" aria-expanded="false" aria-label="Toggle navigation">
          <span className="navbar-toggler-icon"></span>
        </button>
        <div className="collapse navbar-collapse" id="navbarNav">
          <ul className="navbar-nav ms-auto align-items-center" id="top-nav-main-menu">
            {deferredPrompt && (
              <li className="nav-item me-2">
                <button 
                  className="btn btn-sm btn-outline-honey" 
                  onClick={handleInstallClick}
                >
                  Install App
                </button>
              </li>
            )}
            
            {localStorage.getItem('userId') ? (
              <>
                <li className="nav-item"><Link className="nav-link" to="/dashboard">Dashboard</Link></li>
                <li className="nav-item"><Link className="nav-link" to="/schedule">Schedule</Link></li>
                <li className="nav-item"><Link className="nav-link" to="/account">Account</Link></li>
                <SyncIndicator />
                {navOngoingLatest && !isOnSessionRoute && (
                  <li className="nav-item d-flex flex-row align-items-center">
                    <Link
                      className="nav-link px-3 text-warning"
                      to={navOngoingLatest.is_public ? `/publicsession/${navOngoingLatest.id}` : `/solosession/${navOngoingLatest.id}`}
                      onClick={(e) => {
                        try {
                          e.preventDefault();
                          const el = document.getElementById('navbarNav');
                          if (el && el.classList.contains('show')) el.classList.remove('show');
                        } catch {}
                        navigate(navOngoingLatest.is_public ? `/publicsession/${navOngoingLatest.id}` : `/solosession/${navOngoingLatest.id}`);
                      }}
                    >
                      Resume session
                    </Link>
                  </li>
                )}
              </>
            ) : (
              <>
                <li className="nav-item">
                  <Link className="nav-link" to="/login">Login</Link>
                </li>
                <li className="nav-item d-none d-lg-flex align-items-center mx-2">
                  <span className="nav-divider"></span>
                </li>
                <li className="nav-item">
                  <Link className="nav-link btn btn-cta text-white" to="/register">Try FocusBees for Free</Link>
                </li>
              </>
            )}
          </ul>
          <div id="mobile-menu-extras" className="d-lg-none w-100 border-top mt-2 pt-2 empty-d-none"></div>
        </div>
      </div>
    </nav>
    </header>
  );
};

const LayoutWrapper: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const location = useLocation();
  const isLanding = location.pathname === '/';
  const isSchedule = location.pathname === '/schedule';
  const isDashboard = location.pathname === '/dashboard';
  const isSession = location.pathname.startsWith('/solosession/') || location.pathname.startsWith('/publicsession/');
  
  let className = 'pt-nav-offset';
  if (isLanding) className = '';
  else if (isSchedule || isDashboard) className = 'pt-schedule-offset';
  else if (isSession) className = 'pt-session-offset min-vh-100';

  return (
    <div className={className}>
      {children}
    </div>
  );
};

const LandingRedirect: React.FC = () => {
  const [target, setTarget] = useState<string | null>(null);
  const authed = !!storage.getString('userId');

  useEffect(() => {
    if (!authed) return;
    let tier: string | null = null;
    try {
      tier = localStorage.getItem('userTier');
    } catch {}
    if (tier === 'paid' || tier === 'free') {
      setTarget('/schedule');
      return;
    }
    const uid = storage.getString('userId');
    if (!uid) {
      setTarget('/schedule');
      return;
    }
    (async () => {
      try {
        const usage = await api.get(`/users/${uid}/usage`, { skipAuthRedirect: true } as any);
        const rawTier = usage.data?.tier;
        const t = typeof rawTier === 'string' ? rawTier : null;
        if (t === 'free' || t === 'paid') {
          try {
            localStorage.setItem('userTier', t);
          } catch {}
        }
        setTarget('/schedule');
      } catch {
        setTarget('/schedule');
      }
    })();
  }, [authed]);

  if (!authed) return <Home />;
  if (!target) return null;
  return <Navigate to={target} replace />;
};



function App() {
  const [theme] = useState<'dark'>('dark');

  useEffect(() => {
    document.body.setAttribute('data-theme', 'dark');
    localStorage.setItem('app-theme', 'dark');
  }, []);

  const toggleTheme = () => {};

  useEffect(() => {
    try {
      const keys = Object.keys(localStorage || {});
      keys.filter((k) => k.startsWith('sessionStats:')).forEach((k) => {
        try {
          const raw = localStorage.getItem(k);
          if (!raw) return;
          const obj = JSON.parse(raw);
          if (!obj || typeof obj !== 'object') return;
          let changed = false;
          ['todoTotal', 'todoCompleted', 'taskFocusPercent', 'todoCompletionSeries'].forEach((p) => {
            if (p in obj) { delete obj[p]; changed = true; }
          });
          if (changed) localStorage.setItem(k, JSON.stringify(obj));
        } catch {}
      });
      keys.filter((k) => k.startsWith('sessionSummary:')).forEach((k) => {
        try { localStorage.removeItem(k); } catch {}
      });
    } catch {}
  }, []);
  return (
    <ThemeContext.Provider value={{ theme, toggleTheme }}>
      <Router>
        <ErrorBoundary>
          <a href="#main-content" className="skip-link">Skip to main content</a>
          <TopNav />
          <LayoutWrapper>
            <React.Suspense fallback={<div className="container mt-5 text-center">Loading…</div>}>
              {process.env.NODE_ENV === 'development' && <FocusScoreDebug />}
              <Routes>
                <Route path="/" element={<LandingRedirect />} />
                <Route path="/register" element={<Register />} />
                <Route path="/login" element={<Login />} />
                <Route path="/forgot-password" element={<ForgotPassword />} />
                <Route path="/password-reset" element={<ResetPassword />} />
                <Route path="/verify-email" element={<VerifyEmail />} />
                <Route path="/oauth/callback" element={<OAuthCallback />} />
                {/* <Route path="/pricing" element={<Pricing />} /> */}
                <Route path="/blog" element={<BlogList />} />
                <Route path="/blog/:slug" element={<BlogPost />} />
                <Route path="/privacy" element={<PrivacyPolicy />} />
                <Route path="/terms" element={<TermsOfService />} />
                <Route path="/schedule" element={<RequireAuth><CommunitySchedule /></RequireAuth>} />
                <Route path="/dashboard" element={<RequireAuth><Analytics /></RequireAuth>} />
                <Route path="/account" element={<RequireAuth><Account /></RequireAuth>} />
                <Route path="/admin" element={<RequireAuth><AdminPanel /></RequireAuth>} />
                <Route path="/admin/blog" element={<RequireAuth><AdminBlog /></RequireAuth>} />
                <Route path="/camera" element={<RequireAuth><CameraMonitor /></RequireAuth>} />
                <Route path="/solosession/:sessionId" element={<SoloSessionRoom />} />
                <Route path="/publicsession/:sessionId" element={<PublicSessionRoom />} />
                <Route path="/zen/:sessionId" element={<PhoneZen />} />
                <Route path="*" element={<NotFound />} />
              </Routes>
            </React.Suspense>
          </LayoutWrapper>
        </ErrorBoundary>
      </Router>
    </ThemeContext.Provider>
  );
}

export default App;
